"use client";

import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useMemo,
  useState,
} from "react";

type NullableStringOrNumber = string | number | null;

interface LeadFormData {
  source?: NullableStringOrNumber;
  country?: NullableStringOrNumber;
  college?: NullableStringOrNumber;
  exam?: NullableStringOrNumber;
  course?: any;
  title?: string | null;
  isAbroadCountry?: boolean | null;
}

interface GlobalContextType {
  isTransparent: boolean;
  setIsTransparent: Dispatch<SetStateAction<boolean>>;
  isRelatedStudyAbroad: boolean;
  setIsRelatedStudyAbroad: Dispatch<SetStateAction<boolean>>;
  searchBoxOpen: boolean;
  setSearchBoxOpen: Dispatch<SetStateAction<boolean>>;
  isLeadFormOpen: boolean;
  setIsLeadFormOpen: Dispatch<SetStateAction<boolean>>;
  leadFormData: LeadFormData;
  setLeadFormData: Dispatch<SetStateAction<LeadFormData>>;
}

const GlobalContext = createContext<GlobalContextType | undefined>(undefined);

export const GlobalProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const [isTransparent, setIsTransparent] = useState(true);
  const [isRelatedStudyAbroad, setIsRelatedStudyAbroad] = useState(false);
  const [searchBoxOpen, setSearchBoxOpen] = useState(false);
  const [isLeadFormOpen, setIsLeadFormOpen] = useState(false);
  const [leadFormData, setLeadFormData] = useState<LeadFormData>({
    source: null,
    country: null,
    college: null,
    exam: null,
    course: null,
    isAbroadCountry: false,
  });

  const value = useMemo(
    () => ({
      isTransparent,
      setIsTransparent,
      isRelatedStudyAbroad,
      setIsRelatedStudyAbroad,
      searchBoxOpen,
      setSearchBoxOpen,
      isLeadFormOpen,
      setIsLeadFormOpen,
      leadFormData,
      setLeadFormData,
    }),
    [isTransparent, isRelatedStudyAbroad, searchBoxOpen, isLeadFormOpen]
  );

  return (
    <GlobalContext.Provider value={value}>{children}</GlobalContext.Provider>
  );
};

export const useGlobalContext = () => {
  const context = useContext(GlobalContext);
  if (!context) {
    throw new Error("useGlobalContext must be used within a GlobalProvider");
  }
  return context;
};
